/**
 * Service pour interagir avec /api/v1/custom-stats/kpis.
 */

import { nestCall, NestApiType } from '@/composables/nestApi';
import type {
  AvailableField,
  AvailableSource,
  CreateCustomKpiPayload,
  CustomKpiDefinition,
  PreviewKpiPayload,
  PreviewResult,
  UpdateCustomKpiPayload,
} from './customKpi.interfaces';

const BASE = '/custom-stats/kpis';

export async function fetchKpis(): Promise<CustomKpiDefinition[]> {
  const res = await nestCall('get', NestApiType.CLIENT_API, BASE);
  if (res?.error) throw new Error(res.error);
  return (res?.data ?? []) as CustomKpiDefinition[];
}

export async function fetchKpi(id: string): Promise<CustomKpiDefinition> {
  const res = await nestCall(
    'get',
    NestApiType.CLIENT_API,
    `${BASE}/${encodeURIComponent(id)}`,
  );
  if (res?.error) throw new Error(res.error);
  return res?.data as CustomKpiDefinition;
}

export async function createKpi(
  payload: CreateCustomKpiPayload,
): Promise<CustomKpiDefinition> {
  const res = await nestCall(
    'post',
    NestApiType.CLIENT_API,
    BASE,
    {},
    '',
    payload,
  );
  if (res?.error) throw new Error(res.error);
  return res?.data as CustomKpiDefinition;
}

export async function updateKpi(
  id: string,
  payload: UpdateCustomKpiPayload,
): Promise<CustomKpiDefinition> {
  const res = await nestCall(
    'patch',
    NestApiType.CLIENT_API,
    `${BASE}/${encodeURIComponent(id)}`,
    {},
    '',
    payload,
  );
  if (res?.error) throw new Error(res.error);
  return res?.data as CustomKpiDefinition;
}

export async function deleteKpi(
  id: string,
  confirmation: string,
): Promise<void> {
  const res = await nestCall(
    'delete',
    NestApiType.CLIENT_API,
    `${BASE}/${encodeURIComponent(id)}`,
    {},
    '',
    { confirmation },
  );
  if (res?.error) throw new Error(res.error);
}

export async function previewKpi(
  payload: PreviewKpiPayload,
): Promise<PreviewResult> {
  const res = await nestCall(
    'post',
    NestApiType.CLIENT_API,
    `${BASE}/preview`,
    {},
    '',
    payload,
  );
  if (res?.error) throw new Error(res.error);
  return res?.data as PreviewResult;
}

export async function fetchAvailableSources(): Promise<AvailableSource[]> {
  const res = await nestCall(
    'get',
    NestApiType.CLIENT_API,
    `${BASE}/sources`,
  );
  if (res?.error) throw new Error(res.error);
  return (res?.data ?? []) as AvailableSource[];
}

export async function fetchAvailableFields(
  sourceType: 'custom' | 'system',
  sourceSchema: string,
  relationsChain: string[] = [],
): Promise<AvailableField[]> {
  const params: Record<string, string> = {
    sourceType,
    sourceSchema,
  };
  if (relationsChain.length > 0) {
    params.relationsChain = relationsChain.join(',');
  }
  const res = await nestCall(
    'get',
    NestApiType.CLIENT_API,
    `${BASE}/fields`,
    {},
    '',
    undefined,
    true,
    params,
  );
  if (res?.error) throw new Error(res.error);
  return (res?.data ?? []) as AvailableField[];
}
